import React, { useRef } from "react"

const FORM_LINK_CLASS = "text-blue-700 underline cursor-pointer"
const TEXTAREA_CLASS =
  "w-full min-h-[100px] resize-y border border-gray-300 rounded p-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#241763]/30 focus:border-[#241763]"
const TEXTAREA_READ_ONLY_CLASS =
  "w-full min-h-[100px] resize-y border border-gray-200 rounded p-2 text-sm bg-gray-50 text-gray-700"
const INPUT_CLASS =
  "w-full border border-gray-300 rounded p-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#241763]/30 focus:border-[#241763]"
const INPUT_READ_ONLY_CLASS =
  "w-full border border-gray-200 rounded p-2 text-sm bg-gray-50 text-gray-700"

export const EMPTY_END_OF_YEAR_FORM = {
  leaderName: "",
  leaderLocation: "",
  impactOnPractice: "",
  impactOnStudentLearning: "",
  challengesAndNextSteps: "",
  evidenceAttachments: [],
  reflectionDate: "",
  conferenceDate: "",
  summativeFeedback: "",
  developmentImpact: "",
  partnerImpact: "",
  communityImpact: "",
  evaluativeCycleComplete: null,
  tier2Support: false,
  tier2Strategies: "",
  multiYearContinue: false,
  multiYearAdjust: false,
  multiYearCompleted: false,
  multiYearNotes: "",
}

export const mapEndOfYearCheckInToForm = (checkIn) => {
  let evidenceAttachments = []
  try {
    evidenceAttachments = checkIn?.evidenceAttachments
      ? JSON.parse(checkIn.evidenceAttachments)
      : []
  } catch {
    evidenceAttachments = []
  }

  return {
    leaderName: checkIn?.leaderName || checkIn?.adminName || "",
    leaderLocation: checkIn?.leaderLocation || "",
    impactOnPractice: checkIn?.impactOnPractice || "",
    impactOnStudentLearning: checkIn?.impactOnStudentLearning || "",
    challengesAndNextSteps: checkIn?.challengesAndNextSteps || "",
    evidenceAttachments,
    reflectionDate: checkIn?.leaderSignatureDate || "",
    conferenceDate: checkIn?.conferenceDate || "",
    summativeFeedback: checkIn?.summativeFeedback || "",
    developmentImpact: checkIn?.developmentImpact || "",
    partnerImpact: checkIn?.partnerImpact || "",
    communityImpact: checkIn?.communityImpact || "",
    evaluativeCycleComplete:
      checkIn?.evaluativeCycleComplete === true
        ? true
        : checkIn?.evaluativeCycleComplete === false
        ? false
        : null,
    tier2Support: checkIn?.tier2Support || false,
    tier2Strategies: checkIn?.tier2Strategies || "",
    multiYearContinue: checkIn?.multiYearContinue || false,
    multiYearAdjust: checkIn?.multiYearAdjust || false,
    multiYearCompleted: checkIn?.multiYearCompleted || false,
    multiYearNotes: checkIn?.multiYearNotes || "",
  }
}

const REFLECTION_QUESTIONS = [
  {
    field: "impactOnPractice",
    label:
      "What impact did your new learning have on your practice/goal(s), and how do you know?",
  },
  {
    field: "impactOnStudentLearning",
    label:
      "What impact did your new learning and practice have on your student learning, growth, and or achievement, and how do you know?",
  },
  {
    field: "challengesAndNextSteps",
    label:
      "What challenges did you encounter and what are your next steps with your professional learning?",
  },
]

const SUMMATIVE_ROWS = [
  {
    field: "summativeFeedback",
    label: "Summative Feedback",
  },
  {
    field: "developmentImpact",
    label:
      "Development of new learning & impact on leadership practice related to goal(s).",
  },
  {
    field: "partnerImpact",
    label:
      "Impact of new learning and leadership practice on key partners and or organizational outcomes.",
  },
  {
    field: "communityImpact",
    label: "Impact of new learning on greater community.",
  },
]

const FormRow = ({ question, field, value, onChange, readOnly }) => (
  <tr className="border border-gray-400">
    <td className="w-[38%] align-top border border-gray-400 p-3 text-sm bg-white">
      {question}
    </td>
    <td className="w-[62%] align-top border border-gray-400 p-2 bg-white">
      <textarea
        value={value || ""}
        onChange={(e) => onChange(field, e.target.value)}
        readOnly={readOnly}
        rows={5}
        className={readOnly ? TEXTAREA_READ_ONLY_CLASS : TEXTAREA_CLASS}
        aria-label={typeof question === "string" ? question : field}
      />
    </td>
  </tr>
)

const EndOfYearReflectionForm = ({
  formData,
  onChange,
  mode = "admin",
  adminName,
  onUploadEvidence,
  uploadingEvidence = false,
}) => {
  const fileInputRef = useRef(null)
  const completedByName = adminName || formData.leaderName || "Name"

  const isLeaderReadOnly =
    mode === "super_admin_conference" || mode === "view" || mode === "initiate"
  const isConferenceReadOnly = mode === "admin" || mode === "initiate" || mode === "view"

  const handleFileChange = async (event) => {
    const files = Array.from(event.target.files || [])
    if (!files.length || !onUploadEvidence) return

    const oversized = files.find((file) => file.size > 10 * 1024 * 1024)
    if (oversized) {
      alert("Each file must be 10MB or less")
      event.target.value = ""
      return
    }

    await onUploadEvidence(files)
    event.target.value = ""
  }

  const handleRemoveAttachment = (index) => {
    const updated = formData.evidenceAttachments.filter((_, i) => i !== index)
    onChange("evidenceAttachments", updated)
  }

  return (
    <div className="space-y-4">
      {adminName && (
        <p className="text-sm text-gray-600">
          End-of-Year Reflection for:{" "}
          <span className="font-semibold">{adminName}</span>
        </p>
      )}

      <div className="border border-gray-400 overflow-hidden">
        <div className="bg-[#70AD47] text-center font-bold py-2 border-b border-gray-400 text-sm text-white">
          End-of-Year Reflection &amp; Feedback Process
        </div>
        <div className="bg-[#E2EFDA] text-center py-2 border-b border-gray-400 text-sm italic">
          Non-negotiable Process Element of the CT Guidelines (2023)
        </div>

        <table className="w-full border-collapse">
          <tbody>
            <tr className="border border-gray-400">
              <td className="w-1/2 border border-gray-400 p-3 text-sm font-medium bg-white">
                Name:
              </td>
              <td className="w-1/2 border border-gray-400 p-2 bg-white">
                <input
                  type="text"
                  value={formData.leaderName}
                  onChange={(e) => onChange("leaderName", e.target.value)}
                  readOnly={isLeaderReadOnly}
                  className={isLeaderReadOnly ? INPUT_READ_ONLY_CLASS : INPUT_CLASS}
                />
              </td>
            </tr>
            <tr className="border border-gray-400">
              <td className="w-1/2 border border-gray-400 p-3 text-sm font-medium bg-white">
                Location:
              </td>
              <td className="w-1/2 border border-gray-400 p-2 bg-white">
                <input
                  type="text"
                  value={formData.leaderLocation}
                  onChange={(e) => onChange("leaderLocation", e.target.value)}
                  readOnly={isLeaderReadOnly}
                  className={isLeaderReadOnly ? INPUT_READ_ONLY_CLASS : INPUT_CLASS}
                />
              </td>
            </tr>
          </tbody>
        </table>

        <div className="bg-[#E2EFDA] text-center py-2 border-t border-b border-gray-400">
          <div className="font-bold text-sm">Self-Reflection</div>
          <div className="italic text-sm">Completed by {completedByName}</div>
        </div>

        <div className="border-b border-gray-400 p-3 text-sm bg-white text-center">
          See{" "}
          <span className={FORM_LINK_CLASS}>
            Sample Reflection Questions &amp; Professional Learning &amp; Action
            Questions
          </span>
        </div>

        <table className="w-full border-collapse">
          <tbody>
            {REFLECTION_QUESTIONS.map((item) => (
              <FormRow
                key={item.field}
                question={item.label}
                field={item.field}
                value={formData[item.field]}
                onChange={onChange}
                readOnly={isLeaderReadOnly}
              />
            ))}

            <tr className="border border-gray-400">
              <td className="w-[38%] align-top border border-gray-400 p-3 text-sm bg-white font-medium">
                Links to Evidence:
              </td>
              <td className="w-[62%] align-top border border-gray-400 p-3 bg-white">
                {!isLeaderReadOnly && (
                  <div className="mb-3">
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      onChange={handleFileChange}
                      className="hidden"
                      aria-label="Upload evidence files"
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingEvidence}
                      className="px-4 py-2 text-sm bg-[#241763] text-white rounded hover:bg-[#1a1047] disabled:opacity-50"
                    >
                      {uploadingEvidence ? "Uploading..." : "Upload Attachments"}
                    </button>
                    <p className="text-xs text-gray-500 mt-2">
                      Up to 10MB per file. All file types are allowed.
                    </p>
                  </div>
                )}

                {formData.evidenceAttachments?.length > 0 ? (
                  <ul className="space-y-2">
                    {formData.evidenceAttachments.map((file, index) => (
                      <li
                        key={`${file.fileUrl}-${index}`}
                        className="flex items-center justify-between gap-2 text-sm border border-gray-200 rounded p-2"
                      >
                        <a
                          href={file.fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-700 underline truncate"
                        >
                          {file.fileName}
                        </a>
                        {!isLeaderReadOnly && (
                          <button
                            type="button"
                            onClick={() => handleRemoveAttachment(index)}
                            className="text-red-600 text-xs"
                          >
                            Remove
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-gray-500">No evidence uploaded yet.</p>
                )}
              </td>
            </tr>
            <tr className="border border-gray-400">
              <td className="w-1/2 border border-gray-400 p-3 text-sm font-medium bg-white">
                Date:
              </td>
              <td className="w-1/2 border border-gray-400 p-2 bg-white">
                <input
                  type="date"
                  value={formData.reflectionDate}
                  onChange={(e) => onChange("reflectionDate", e.target.value)}
                  readOnly={isLeaderReadOnly}
                  className={isLeaderReadOnly ? INPUT_READ_ONLY_CLASS : INPUT_CLASS}
                  aria-label="Reflection date"
                />
              </td>
            </tr>
          </tbody>
        </table>

        <div className="bg-[#70AD47] text-center py-2 border-t border-gray-400 text-white">
          <div className="font-bold text-sm">End-of-Year Conference</div>
          <div className="italic text-sm">Completed by Evaluator (by Mid June)</div>
        </div>

        <div className="border-b border-gray-400 p-3 bg-white">
          <label className="text-sm font-medium block mb-1">Date:</label>
          <input
            type="date"
            value={formData.conferenceDate}
            onChange={(e) => onChange("conferenceDate", e.target.value)}
            readOnly={isConferenceReadOnly}
            className={isConferenceReadOnly ? INPUT_READ_ONLY_CLASS : INPUT_CLASS}
            aria-label="Conference date"
          />
        </div>

        <div className="bg-[#E2EFDA] text-center py-2 border-b border-gray-400">
          <div className="font-bold text-sm">Summative Feedback &amp; Growth Criteria</div>
          <div className="italic text-sm">Completed by Evaluator</div>
          <div className="text-sm">
            <span className={FORM_LINK_CLASS}>See appendix for full description</span>
          </div>
        </div>

        <table className="w-full border-collapse">
          <tbody>
            {SUMMATIVE_ROWS.map((item) => (
              <FormRow
                key={item.field}
                question={item.label}
                field={item.field}
                value={formData[item.field]}
                onChange={onChange}
                readOnly={isConferenceReadOnly}
              />
            ))}
          </tbody>
        </table>

        <div className="border border-gray-400 p-3 bg-white">
          <div className="font-medium text-sm mb-2">
            Successful Completion of the Evaluative Cycle
          </div>
          <div className="flex gap-6">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="evaluativeCycleComplete"
                checked={formData.evaluativeCycleComplete === true}
                onChange={() => onChange("evaluativeCycleComplete", true)}
                disabled={isConferenceReadOnly}
                className="h-4 w-4"
              />
              Yes
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="evaluativeCycleComplete"
                checked={formData.evaluativeCycleComplete === false}
                onChange={() => onChange("evaluativeCycleComplete", false)}
                disabled={isConferenceReadOnly}
                className="h-4 w-4"
              />
              No
            </label>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 border-t border-gray-400">
          <div className="border border-gray-400 p-3 bg-white">
            <div className="font-bold text-sm mb-2">Supports Required/Suggested</div>
            <p className="text-sm mb-3">
              Are tiered supports required above and beyond tier 1 (included in
              feedback above)?
            </p>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={formData.tier2Support}
                onChange={(e) => onChange("tier2Support", e.target.checked)}
                disabled={isConferenceReadOnly}
                className="h-4 w-4"
              />
              Tier 2
            </label>
          </div>
          <div className="border border-gray-400 p-3 bg-white">
            <div className="font-bold text-sm mb-2">
              If Tier 2, please specify strategies:
            </div>
            <textarea
              value={formData.tier2Strategies}
              onChange={(e) => onChange("tier2Strategies", e.target.value)}
              readOnly={isConferenceReadOnly}
              rows={4}
              className={isConferenceReadOnly ? TEXTAREA_READ_ONLY_CLASS : TEXTAREA_CLASS}
              aria-label="Tier 2 strategies"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 border-t border-gray-400">
          <div className="border border-gray-400 p-3 bg-[#E2EFDA] text-sm">
            <div className="font-bold mb-2">For multi-year goals only:</div>
            <ul className="list-disc pl-5 space-y-1">
              <li>What adjustments are needed to the goal(s)?</li>
              <li>Why?</li>
              <li>How might adjustments impact the timing of the goal(s)?</li>
            </ul>
          </div>
          <div className="border border-gray-400 p-3 bg-white">
            <div className="space-y-2 mb-3">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={formData.multiYearContinue}
                  onChange={(e) => onChange("multiYearContinue", e.target.checked)}
                  disabled={isConferenceReadOnly}
                  className="h-4 w-4"
                />
                Leader will continue multi-year goal.
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={formData.multiYearAdjust}
                  onChange={(e) => onChange("multiYearAdjust", e.target.checked)}
                  disabled={isConferenceReadOnly}
                  className="h-4 w-4"
                />
                Leader will adjust multi-year goal.
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={formData.multiYearCompleted}
                  onChange={(e) => onChange("multiYearCompleted", e.target.checked)}
                  disabled={isConferenceReadOnly}
                  className="h-4 w-4"
                />
                Leader completed multi-year goal.
              </label>
            </div>
            <div className="font-bold text-sm mb-1">Notes:</div>
            <textarea
              value={formData.multiYearNotes}
              onChange={(e) => onChange("multiYearNotes", e.target.value)}
              readOnly={isConferenceReadOnly}
              rows={4}
              className={isConferenceReadOnly ? TEXTAREA_READ_ONLY_CLASS : TEXTAREA_CLASS}
              aria-label="Multi-year goal notes"
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default EndOfYearReflectionForm
